import React, { useState, useRef, useEffect } from "react";
import TouchScoringCard from "./TouchScoringCard";

// Hook to detect container size for responsive preview
const useContainerSize = (containerRef) => {
  const [containerSize, setContainerSize] = useState('medium');

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const { width } = rect;
        
        // Determine size based on container dimensions
        if (width < 400) {
          setContainerSize('small');
        } else if (width < 600) {
          setContainerSize('medium');
        } else if (width < 800) {
          setContainerSize('large');
        } else {
          setContainerSize('xlarge');
        }
      }
    };

    // Use ResizeObserver for better performance than window resize
    const resizeObserver = new ResizeObserver(updateContainerSize);
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      updateContainerSize(); // Initial measurement
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return containerSize;
};

/**
 * CriteriaPreview component that shows how criteria will appear to judges
 * Uses the actual TouchScoringCard component from the scoring interface
 */
const CriteriaPreview = ({ 
  criteria = [], 
  isDarkMode = false,
  className = "",
  showTitle = true 
}) => {
  const containerRef = useRef(null);
  const containerSize = useContainerSize(containerRef);
  
  // State to track preview scores for each criterion
  const [previewScores, setPreviewScores] = useState({});

  // Initialize preview scores when criteria changes
  useEffect(() => {
    const initialScores = {};
    criteria.forEach(criterion => {
      // Set initial score to about 70% of max points for a realistic preview
      const maxPoints = criterion.maxPoints || 0;
      initialScores[criterion.id] = Math.round(maxPoints * 0.7 * 2) / 2; // Round to nearest 0.5
    });
    setPreviewScores(initialScores);
  }, [criteria]);

  const handleScoreChange = (criterionId, newScore) => {
    setPreviewScores(prev => ({
      ...prev,
      [criterionId]: newScore
    }));
  };

  if (criteria.length === 0) {
    return (
      <div ref={containerRef} className={`text-center py-8 ${className}`}>
        <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Add criteria to see preview
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      {showTitle && (
        <div className={`mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <h3 className="text-lg font-semibold">Live Preview</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            See how your criteria will appear to judges
          </p>
        </div>
      )}
      
      <div className="space-y-3">
        {criteria.map((criterion) => {
          const maxPoints = criterion.maxPoints || 0;
          const currentScore = previewScores[criterion.id] || 0;
          const criterionName = criterion.label || criterion.name || `Criterion ${criterion.id}`;

          return (
            <TouchScoringCard
              key={criterion.id}
              criterion={criterion}
              criterionName={criterionName}
              currentScore={currentScore}
              maxPoints={maxPoints}
              isDarkMode={isDarkMode}
              containerSize={containerSize}
              isPreviewMode={true}
              onScoreChange={(value) => handleScoreChange(criterion.id, value)}
            />
          );
        })}
      </div>
      
      {/* Preview note */}
      <div className={`mt-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} text-center`}>
        Interactive preview - try clicking and dragging on the cards above
      </div>
    </div>
  );
};

export default CriteriaPreview;
